import { TopicType, TopicTypes } from '@/utils/constants'
import { ConnectionModel } from './connection'
import { Note, Topic } from './topic'

// export enum ActivityType {
//   LIKE = 1,
//   COMMENT = 2,
//   FOLLOW = 3,
// }

export enum SocketEventType {
  USER_LOGGED_IN = 'User_login',
  USER_BECAME_ONLINE = 'User_online',
  USER_GONE_OFFLINE = 'User_offline',
  CONNECTION_REQUESTED = 'connection_requested',
  USER_CONNECTED = 'connected',
  TOPIC_ADDED = 'topic_added',
  TOPIC_SHARED = 'topic_shared',
  TOPIC_UPDATED = 'topic_updated',
  TOPIC_DELETED = 'topic_deleted',
  TOPIC_VIEWED = 'topic_view',
  TOPIC_NOTE_LIKED = 'Note_Thumbs_Up',
  TOPIC_NOTE_UNLIKED = 'Note_Thumbs_Down',
  TOPIC_NOTE_ADDED = 'topic_note_added',
  TOPIC_NOTE_UPDATED = 'topic_note_updated',
  TOPIC_NOTE_DELETED = 'topic_note_deleted',
  NOTE_COMMENT_ADDED = 'Note_comments',
  NOTE_COMMENT_DELETED = 'Note_comments_deleted',
  TOPIC_PHOTO_ADDED = 'topic_photo_added',
  TOPIC_PHOTO_DELETED = 'topic_photo_deleted',
  PHOTO_COMMENT_ADDED = 'comments',
  PHOTO_COMMENT_DELETED = 'photo_comments_deleted',
  PHOTO_LIKED = 'like',
  PHOTO_UNLIKED = 'dislike',
}

export class ActivityTypeUtil {
  static getActivityText(event: NotificationItem): string {
    const { eventType, topicType } = event

    const topicTypeTitle = this.isTopicRelatedEvent(eventType)
      ? TopicTypes[Number(topicType)].title?.toLowerCase()
      : ''

    let result = ''
    switch (eventType) {
      case SocketEventType.TOPIC_NOTE_LIKED:
        result = `liked a ${topicTypeTitle}`
        break

      case SocketEventType.TOPIC_NOTE_UNLIKED:
        result = `unliked a ${topicTypeTitle}`
        break

      case SocketEventType.NOTE_COMMENT_ADDED:
        result = `commented on a ${topicTypeTitle}`
        break
      case SocketEventType.USER_CONNECTED:
        result = 'is now a connection'
        break

      case SocketEventType.TOPIC_VIEWED:
        result = `viewed a ${topicTypeTitle}`
        break
    }
    return result
  }

  private static readonly SUPPORTED_EVENTS = new Set<SocketEventType>([
    SocketEventType.TOPIC_VIEWED,
    SocketEventType.TOPIC_NOTE_LIKED,
    SocketEventType.TOPIC_NOTE_UNLIKED,
    SocketEventType.NOTE_COMMENT_ADDED,
    SocketEventType.USER_CONNECTED,
  ])

  private static readonly STORAGE_SUPPORTED_EVENTS = new Set<SocketEventType>([
    SocketEventType.TOPIC_VIEWED,
    SocketEventType.TOPIC_NOTE_LIKED,
    SocketEventType.NOTE_COMMENT_ADDED,
    SocketEventType.USER_CONNECTED,
  ])

  static isSocketEventSupported(eventType: SocketEventType): boolean {
    return this.SUPPORTED_EVENTS.has(eventType)
  }

  static isSocketEventStorageSupported(eventType: SocketEventType): boolean {
    return this.STORAGE_SUPPORTED_EVENTS.has(eventType)
  }

  static isTopicRelatedEvent = (eventType: SocketEventType): boolean => {
    const allOther = [
      SocketEventType.CONNECTION_REQUESTED,
      SocketEventType.USER_CONNECTED,
      SocketEventType.USER_BECAME_ONLINE,
      SocketEventType.USER_GONE_OFFLINE,
      SocketEventType.USER_LOGGED_IN,
    ]
    return !allOther.includes(eventType)
  }

  static isTopicLevelEvent = (eventType: SocketEventType): boolean => {
    const topicLevel = [
      SocketEventType.TOPIC_VIEWED,
      SocketEventType.TOPIC_ADDED,
      SocketEventType.TOPIC_SHARED,
    ]
    return topicLevel.includes(eventType)
  }

  static getTopicID(item: NotificationItem): string {
    const { eventType, objectID, secondID } = item
    return this.isTopicLevelEvent(eventType) ? objectID : secondID
  }

  static isNoteLevelEvent = (eventType: SocketEventType): boolean => {
    const noteLevel = [
      SocketEventType.TOPIC_NOTE_UPDATED,
      SocketEventType.TOPIC_NOTE_ADDED,
      SocketEventType.TOPIC_NOTE_DELETED,
      SocketEventType.TOPIC_NOTE_LIKED,
      SocketEventType.TOPIC_NOTE_UNLIKED,
    ]
    return noteLevel.includes(eventType)
  }

  static getNoteID(item: NotificationItem): string {
    return item.objectID
  }

  static getTopicTitle(item: NotificationItem): string {
    const { eventType, objectName, secondName } = item
    return this.isTopicLevelEvent(eventType) ? objectName : secondName
  }

  static getCommentText(item: NotificationItem): string {
    return SocketEventType.NOTE_COMMENT_ADDED ? item.thirdName : ''
  }
}

export interface SocketMessage {
  userName: string
  eventData: EventData
}

export interface EventData {
  eventID: string
  eventType: SocketEventType
  eventDateTime: string
  userID: string
  userName: string
  userFullName: string
  sourceUserID: string
  sourceUserName: string
  sourceUserFullName: string
  profilePicURL: string
  profilePicThumbURL: string
  profilePicFullURL: string
  objectID: string
  objectName: string
  secondID: string
  secondName: string
  thirdID: string
  thirdName: string
  variableData: string
}

export interface NotificationItem extends EventData {
  isRead: boolean
  topicApplication: number
  topicType: TopicType
  visibility: number
  extraData: Record<string, any>[]
}

export interface NotificationGroup extends NotificationItem {
  count: number
}
